import '@/styles/Map.scss';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { Map as MapboxMap } from 'mapbox-gl';
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ErrorDisplay, LoadingOverlay } from '@/components/atoms/primitives';
import { MapControls, MapPhaseIndicator, WindLegend } from '@/components/molecules';
import { MapLayersManager, MapViewport } from '@/components/organisms';
import { MAP_LAYERS_PANEL_CONTROLS_GAP_PX, MAP_LAYERS_PANEL_WIDTH_PX } from '@/constants/mapLayout';
import { MapboxProvider, useMapbox } from '@/contexts/MapboxContext';
import {
  AreaTrafficOverlay,
  CdcAnimationOverlay,
  CdcConnectionLines,
  CdcGraphExplorerButton,
  CdcNearbyRadius,
  CdcSelectedVesselHighlight,
  CdcVesselLayer,
  useRectangleDraw,
} from '@/feat/cdc';
import { useCdcStore } from '@/feat/cdc/stores/useCdcStore';
import { useGraphExplorerStore } from '@/feat/cdc/stores/useGraphExplorerStore';
import { usePolygon } from '@/hooks/usePolygon';
import { imagePreloader } from '@/services/map/ImagePreloader';
import { mapManager } from '@/services/map/MapManager';
import { useAuthStore } from '@/stores/slices/useAuthStore';
import { useConfigStore } from '@/stores/slices/useConfigStore';
import { useMapLayoutStore } from '@/stores/slices/useMapLayoutStore';
import { useMapStore } from '@/stores/slices/useMapStore';
import { usePanelStore } from '@/stores/slices/usePanelStore';
import { useUIStore } from '@/stores/slices/useUIStore';
import { useGlobeSpin } from './hooks/useGlobeSpin';
import type { MapPageProps } from './MapPage.types';

// Lazy load components that aren't needed immediately
const Sidebar = lazy(() => import('@/components/organisms/Sidebar'));
const LayersPanel = lazy(() =>
  import('@/components/organisms/LayersPanel').then((module) => ({ default: module.LayersPanel })),
);
const NewsPanel = lazy(() =>
  import('@/components/organisms/NewsPanel').then((module) => ({ default: module.NewsPanel })),
);
const AIAssistantPanel = lazy(() =>
  import('@/components/organisms/AIAssistantPanel').then((module) => ({ default: module.AIAssistantPanel })),
);
const RulerToolPanel = lazy(() =>
  import('@/components/organisms/RulerToolPanel').then((module) => ({ default: module.RulerToolPanel })),
);
const GraphExplorer = lazy(() =>
  import('@/feat/cdc/components/GraphExplorer').then((module) => ({ default: module.GraphExplorer })),
);
const CartPanel = lazy(() =>
  import('@/components/organisms/TaskingOrdersPanel').then((module) => ({ default: module.CartPanel })),
);
const CorridorFlythrough = lazy(() => import('@/components/organisms/CorridorFlythrough/CorridorFlythrough'));
const CorridorLegend = lazy(() =>
  import('@/components/molecules/CorridorLegend/CorridorLegend').then((module) => ({
    default: module.CorridorLegend,
  })),
);

// Lazy load addStarterPopups - only needed when globe view is clicked
let addStarterPopups: ((map: MapboxMap, onPopupClick?: (type: string) => void) => void) | null = null;
const loadStarterPopups = async () => {
  if (!addStarterPopups) {
    const module = await import('@/services/map/layers/starterPopups');
    addStarterPopups = module.addStarterPopups;
  }
  return addStarterPopups;
};

function MapPageContent({ getToken }: MapPageProps) {
  const container = useRef<HTMLDivElement>(null);
  const { map, isMapReady } = useMapbox();
  const location = useLocation();
  const isTaskingRoute = location.pathname === '/tasking';

  // Use stores with granular selectors for performance
  const mapLoading = useMapStore((state) => state.mapLoading);
  const initialLoadComplete = useMapStore((state) => state.initialLoadComplete);
  const setMapLoading = useMapStore((state) => state.setMapLoading);
  const setInitialLoadComplete = useMapStore((state) => state.setInitialLoadComplete);
  const setLayerStatus = useMapStore((state) => state.setLayerStatus);

  const rulerToolOpen = useUIStore((state) => state.rulerToolOpen);
  const setRulerToolOpen = useUIStore((state) => state.setRulerToolOpen);
  const layersPanelExpanded = useMapLayoutStore((state) => state.layersPanelExpanded);
  const newsPanelExpanded = useMapLayoutStore((state) => state.newsPanelExpanded);
  const aiAssistantPanelExpanded = useMapLayoutStore((state) => state.aiAssistantPanelExpanded);

  const setPanel = usePanelStore((state) => state.setPanel);

  const isGraphExplorerOpen = useGraphExplorerStore((state) => state.isOpen);
  const graphExplorerMmsi = useGraphExplorerStore((state) => state.centerMmsi);
  const graphExplorerName = useGraphExplorerStore((state) => state.centerVesselName);
  const closeGraphExplorer = useGraphExplorerStore((state) => state.close);
  const setSelectedMmsi = useCdcStore((state) => state.setSelectedMmsi);
  const setSelectedVesselCoords = useCdcStore((state) => state.setSelectedVesselCoords);

  const config = useConfigStore((state) => state.config);
  const configError = useConfigStore((state) => state.configError);
  const setConfig = useConfigStore((state) => state.setConfig);
  const saveConfig = useConfigStore((state) => state.saveConfig);
  const clearSaveTimeout = useConfigStore((state) => state.clearSaveTimeout);

  // Get selected polygon WKT for vessel fetching
  const selectedPolygon = useMapStore((state) => state.selectedPolygon);
  const selectedPolygonWkt = useMemo(() => selectedPolygon?.polygon || null, [selectedPolygon?.polygon]);

  // Initialize config from user when available
  const userFromStore = useAuthStore((state) => state.user);
  useEffect(() => {
    if (
      userFromStore?.config &&
      typeof userFromStore.config === 'object' &&
      userFromStore.config !== null &&
      !Array.isArray(userFromStore.config) &&
      !config &&
      !configError
    ) {
      setConfig(userFromStore.config as Record<string, unknown>);
    }
  }, [userFromStore?.config, config, configError, setConfig]);

  const { spinGlobe, handleUserInteraction } = useGlobeSpin(map);

  // Use polygon hook for automatic polygon plotting
  usePolygon();

  // Area Traffic rectangle draw hook
  useRectangleDraw();

  // Memoize panel setter callback for popups
  const handlePopupClick = useCallback(
    (type: string) => {
      if (type === 'Incident Reported') {
        setPanel({ name: 'Piracy Incidents', content: null });
      }
      if (type === 'Rule Violation') {
        setPanel({ name: 'Rules and Alerts', content: null });
      }
    },
    [setPanel],
  );

  // Memoize onStyleLoad to prevent map re-initialization when dependencies change
  const handleStyleLoad = useCallback(async () => {
    // Hide loading as soon as map is ready - don't wait for all layers
    setMapLoading(false);
    setInitialLoadComplete(true);
    setLayerStatus('map-style', 'ready');

    // Initialize MapManager with map instance immediately
    if (map.current) {
      mapManager.initialize(map.current);

      // Preload ALL custom icons (vessels, military, incidents, etc.) immediately
      // This ensures each icon is fetched at most once per session
      imagePreloader.preloadAllIcons(map.current);
    }
  }, [map, setMapLoading, setInitialLoadComplete, setLayerStatus]);

  // Mark map-style as loading on first mount
  useEffect(() => {
    setLayerStatus('map-style', 'loading');
  }, [setLayerStatus]);

  // Ensure map is visible once ready; initial spin starts after default layers initialize
  useEffect(() => {
    if (isMapReady && map.current) {
      // Force map to render and ensure it's visible
      requestAnimationFrame(() => {
        if (map.current) {
          map.current.triggerRepaint();
          // Ensure map container is visible
          const mapContainer = map.current.getContainer();
          if (mapContainer) {
            mapContainer.style.visibility = 'visible';
            mapContainer.style.opacity = '1';
          }
        }
      });
    }
  }, [isMapReady, map]);

  // Handle globe view button click - lazy load popups on demand
  const handleGlobeView = useCallback(async () => {
    if (!map.current) return;

    // Load starter popups only when needed
    const popupsFn = await loadStarterPopups();
    popupsFn(map.current, handlePopupClick);

    map.current.flyTo({ zoom: 1.5, pitch: 0, bearing: 0 });
    spinGlobe();
  }, [map, handlePopupClick, spinGlobe]);

  // Memoize location click handler
  const handleLocationClick = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation: [number, number] = [position.coords.longitude, position.coords.latitude];
          setTimeout(() => {
            if (map.current) {
              map.current.flyTo({ center: userLocation, zoom: 5, pitch: 0, bearing: 0 });
            }
          }, 2000);
        },
        () => {},
      );
    }
  }, [map]);

  // Memoize ruler toggle handler
  const handleRulerToggle = useCallback(() => {
    setRulerToolOpen(!rulerToolOpen);
  }, [rulerToolOpen, setRulerToolOpen]);

  const handleRulerClose = useCallback(() => {
    setRulerToolOpen(false);
  }, [setRulerToolOpen]);

  // Keep Mapbox built-in controls (e.g. NavigationControl) in front of / outside the expanded panel
  useEffect(() => {
    if (!map.current) return;

    const mapContainer = map.current.getContainer();
    const ctrl = mapContainer.querySelector('.mapboxgl-ctrl-bottom-right') as HTMLElement | null;
    if (!ctrl) return;

    const panelExpanded = layersPanelExpanded || newsPanelExpanded || aiAssistantPanelExpanded;
    const offsetPx = panelExpanded ? MAP_LAYERS_PANEL_WIDTH_PX + MAP_LAYERS_PANEL_CONTROLS_GAP_PX : 0;
    ctrl.style.right = offsetPx > 0 ? `${offsetPx}px` : '';
    ctrl.style.transition = 'right 250ms';

    return () => {
      ctrl.style.right = '';
      ctrl.style.transition = '';
    };
  }, [layersPanelExpanded, newsPanelExpanded, aiAssistantPanelExpanded, map]);

  // Non-blocking config save - runs in background after initial render
  useEffect(() => {
    // Skip if config is invalid or map not ready
    if (!config || configError || !map.current) {
      return;
    }

    // Save config using store method (handles debouncing internally)
    saveConfig(getToken);

    return () => {
      clearSaveTimeout();
    };
  }, [config, configError, map, getToken, saveConfig, clearSaveTimeout]);

  // Determine what to render based on config state
  // IMPORTANT: Always render map container so map can initialize immediately
  const renderContent = useMemo(() => {
    if (configError) {
      return (
        <ErrorDisplay
          title="Error retrieving your credentials"
          message="Contact Space-Eyes (support@space-eyes.com) for assistance."
        />
      );
    }
    return null; // Don't block map rendering - show loading overlay instead
  }, [configError]);

  return (
    <div className="map">
      {/* Only show loading spinner on initial load, not when fetching vessels */}
      <LoadingOverlay
        variant="spinner"
        overlay
        isVisible={mapLoading && !initialLoadComplete}
        title="Preparing your dashboard"
        message="Setting up assets & analytics..."
        spinnerSize="lg"
      />
      {/* Show config loading overlay if config is not loaded yet */}
      <LoadingOverlay variant="progress" overlay isVisible={!config && !configError} />
      {renderContent || (
        <>
          {/* Map container - ALWAYS render so map can initialize immediately */}
          <MapViewport
            containerRef={container}
            onStyleLoad={handleStyleLoad}
            onUserInteraction={handleUserInteraction}
          />
          <MapPhaseIndicator />
          <Suspense fallback={null}>
            <Sidebar getToken={getToken} />
          </Suspense>
          <MapControls
            onLocationClick={handleLocationClick}
            onGlobeClick={handleGlobeView}
            onRulerToggle={handleRulerToggle}
            rulerOpen={rulerToolOpen}
          />
          <WindLegend />
          <Suspense fallback={null}>
            <CorridorLegend />
          </Suspense>
          {rulerToolOpen && (
            <Suspense fallback={null}>
              <RulerToolPanel isOpen={rulerToolOpen} onClose={handleRulerClose} />
            </Suspense>
          )}
          <Suspense fallback={null}>
            <LayersPanel />
          </Suspense>
          <CdcGraphExplorerButton />
          <Suspense fallback={null}>
            <NewsPanel getToken={getToken} />
          </Suspense>
          <Suspense fallback={null}>
            <AIAssistantPanel getToken={getToken} />
          </Suspense>
          {isTaskingRoute && (
            <Suspense fallback={null}>
              <CartPanel />
            </Suspense>
          )}
          <MapLayersManager
            getToken={getToken}
            polygonWkt={selectedPolygonWkt}
            onPopupClick={handlePopupClick}
            onLayersInitialized={() => {
              // Start globe spin only after default layers (including EEZ) are ready
              spinGlobe();

              // Layers initialized, ensure map is visible
              if (map.current) {
                requestAnimationFrame(() => {
                  if (map.current) {
                    map.current.triggerRepaint();
                    const mapContainer = map.current.getContainer();
                    if (mapContainer) {
                      mapContainer.style.visibility = 'visible';
                      mapContainer.style.opacity = '1';
                    }
                  }
                });
              }
            }}
          />
          {/* CDC VesselLayer handles vessel rendering and store sync - no polygon required */}
          <CdcNearbyRadius />
          <CdcVesselLayer />
          <CdcConnectionLines />
          <CdcSelectedVesselHighlight />
          <CdcAnimationOverlay />
          <AreaTrafficOverlay />

          {isGraphExplorerOpen && graphExplorerMmsi && (
            <Suspense fallback={null}>
              <GraphExplorer
                mmsi={graphExplorerMmsi}
                vesselName={graphExplorerName || graphExplorerMmsi}
                onClose={() => {
                  closeGraphExplorer();
                  // Fly main map to the last selected vessel's location
                  const coords = useCdcStore.getState().selectedVesselCoords;
                  if (
                    map.current &&
                    coords &&
                    coords.lat >= -90 &&
                    coords.lat <= 90 &&
                    coords.lon >= -180 &&
                    coords.lon <= 180
                  ) {
                    map.current.flyTo({
                      center: [coords.lon, coords.lat],
                      zoom: 10,
                      duration: 1500,
                    });
                  }
                }}
                onVesselSelect={(nextMmsi, coords) => {
                  setSelectedMmsi(nextMmsi);
                  if (coords) {
                    setSelectedVesselCoords(coords);
                  }
                }}
              />
            </Suspense>
          )}
        </>
      )}
    </div>
  );
}

// Export wrapper that provides Mapbox context
// Memoize MapPage to prevent re-renders when parent component updates
export const MapPage = React.memo(function MapPage({ getToken }: MapPageProps) {
  return (
    <MapboxProvider>
      <MapPageContent getToken={getToken} />
    </MapboxProvider>
  );
});
